import { useQuery } from '@tanstack/react-query';
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  LayoutDashboard,
  ListChecks,
  LogOut,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  RefreshCw,
  Send,
  Settings,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ThemeToggle } from '@/_shared/components/ThemeToggle';
import { Avatar, AvatarFallback, AvatarImage } from '@/_shared/components/ui/avatar';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/_shared/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { Empty, EmptyDescription } from '@/_shared/components/ui/empty';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { Separator } from '@/_shared/components/ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarSeparator,
  useSidebar,
} from '@/_shared/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';
import { type RudderstackAccessDenial, useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { FeatureFlagsTrigger } from '@/_shared/feature-flags/FeatureFlagsTrigger';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useLocalStorage } from '@/_shared/hooks/useLocalStorage';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import {
  inviteRoleLabel,
  type PendingInvite,
  useAcceptInvite,
  usePendingInvites,
} from '@/_shared/hooks/usePendingInvites';
import { isSetupOnHome, useSetupProgress } from '@/_shared/hooks/useSetupProgress';
import { accessDenialBadge } from '@/_shared/lib/rudderstack-access';
import { SETUP_STEP_TOTAL } from '@/_shared/lib/setup-steps';
import { cn } from '@/_shared/lib/utils';

import { useStarred } from '../hooks/useStarred';
import { RudderstackBrandLogo } from './RudderstackBrandLogo';

const RECENT_CHATS_LIMIT = 5;

interface SidebarShortcutItem {
  id: string;
  title: string;
  to: string;
}

export function AppSidebar(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, toggleSidebar, isMobile } = useSidebar();
  const { slug, workspace, wp, isWorkflowsV2, isAudiences } = useWorkspace();
  const { user, workspaces, signOutAndRedirect } = useAuth();
  const orpc = useOrpc();
  // ACT2-1108: "Setup N of 5" reopens the setup panel on the chat home until setup is complete. Admins only:
  // the hook reads nothing for a member.
  const setup = useSetupProgress();
  const showSetupEntry = setup.status !== null && !setup.complete;
  const invites = usePendingInvites().data?.invites ?? [];
  const acceptInvite = useAcceptInvite();
  const inviteProps = {
    invites,
    onAcceptInvite: (inviteId: string) => acceptInvite.mutate(inviteId),
    acceptPending: acceptInvite.isPending,
  };
  const switcherLabel =
    invites.length > 0
      ? `Switch workspace, ${invites.length} ${invites.length === 1 ? 'invite' : 'invites'}`
      : 'Switch workspace';

  const isCollapsed = state === 'collapsed' && !isMobile;

  const {
    data: sessionsData,
    isLoading: sessionsLoading,
    isError: sessionsError,
    isFetching: sessionsFetching,
    refetch: refetchSessions,
  } = useQuery({
    ...orpc.chat.list.queryOptions({ input: {} }),
    enabled: !isCollapsed,
  });
  const { data: dashboardsData } = useQuery({
    ...orpc.dashboard.list.queryOptions({ input: {} }),
    enabled: !isCollapsed,
  });
  const starred = useStarred('dashboard', { enabled: !isCollapsed });

  // Path matching for nav highlighting
  const p = location.pathname;
  const wsPrefix = `/w/${slug}`;
  const isUnder = (path: string): boolean => p === `${wsPrefix}${path}` || p.startsWith(`${wsPrefix}${path}/`);
  const isHome = p === wsPrefix || p === `${wsPrefix}/`;
  // While the home shows the setup panel, the home is "Setup": that entry is the active one, not New chat.
  const isSetupActive = isHome && isSetupOnHome(setup);
  const isNewChatActive = isHome && !isSetupActive;
  const isDashboardListActive = p === `${wsPrefix}/dashboards`;
  const isDashboardDetailActive = p.startsWith(`${wsPrefix}/d/`);
  const isSettingsActive = p.startsWith(`${wsPrefix}/settings`);

  // chat.list is already newest-first, so the first N are the most recent chats
  const recentChats = useMemo<SidebarShortcutItem[]>(
    () =>
      (sessionsData?.sessions ?? []).slice(0, RECENT_CHATS_LIMIT).map((s) => ({
        id: s.id,
        title: s.title ?? 'New conversation',
        to: wp(`/chat/${s.id}`),
      })),
    [sessionsData, wp],
  );

  const starredDashboards = useMemo<SidebarShortcutItem[]>(() => {
    const dashboardsById = new Map((dashboardsData?.dashboards ?? []).map((dashboard) => [dashboard.id, dashboard]));
    return starred.items.flatMap((favorite) => {
      const dashboard = dashboardsById.get(favorite.entityId);
      return dashboard ? [{ id: dashboard.id, title: dashboard.title, to: wp(`/d/${dashboard.slug}`) }] : [];
    });
  }, [dashboardsData?.dashboards, starred.items, wp]);

  const handleWorkspaceSwitch = (wsId: string) => {
    if (wsId !== workspace.id) {
      void navigate(`/w/${wsId}`);
    }
  };

  // A starred dashboard's page highlights its Starred row instead of the Dashboards item
  const activeStarredDashboard = starredDashboards.find((item) => item.to === p);
  const activeRecentChat = recentChats.find((item) => item.to === p);
  const isStarredDashboardPage = !isCollapsed && activeStarredDashboard !== undefined;

  const renderStarredRow = (item: SidebarShortcutItem): React.ReactElement => (
    <SidebarMenuItem key={item.id}>
      <SidebarMenuButton asChild isActive={p === item.to}>
        <Link to={item.to} aria-current={p === item.to ? 'page' : undefined}>
          <LayoutDashboard className="size-4" />
          <span>{item.title}</span>
        </Link>
      </SidebarMenuButton>
      <SidebarMenuAction
        type="button"
        showOnHover
        aria-label={`Remove ${item.title} from Starred`}
        title="Remove from Starred"
        disabled={starred.isToggling(item.id)}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          starred.toggleStar(item.id, false);
          toast.success('Removed from Starred', {
            action: { label: 'Undo', onClick: () => starred.toggleStar(item.id, true) },
          });
        }}
      >
        <X />
      </SidebarMenuAction>
    </SidebarMenuItem>
  );

  const renderRecentChatRow = (item: SidebarShortcutItem): React.ReactElement => (
    <SidebarMenuItem key={item.id}>
      <SidebarMenuButton asChild isActive={p === item.to}>
        <Link to={item.to} aria-current={p === item.to ? 'page' : undefined}>
          <MessageSquare className="size-4" />
          <span>{item.title}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  const navItems = [
    {
      label: 'New chat',
      to: wp(),
      icon: Plus,
      active: isNewChatActive,
    },
    {
      label: 'Dashboards',
      to: wp('/dashboards'),
      icon: LayoutDashboard,
      active: isDashboardListActive || (isDashboardDetailActive && !isStarredDashboardPage),
    },
    // Workflows nav item: hidden when flag is off; no COMING SOON badge when enabled.
    // isWorkflowsV2 is server-derived from auth.me — not a localStorage dev flag.
    ...(isWorkflowsV2
      ? [
          {
            label: 'Workflows',
            to: wp('/workflows'),
            icon: Zap,
            active: isUnder('/workflows'),
          },
        ]
      : []),
    // Audiences nav item: shown by default (audiences are GA), hidden only for a workspace the server
    // reports as audiences-disabled. Same server-derived gating as Workflows.
    ...(isAudiences
      ? [
          {
            label: 'Audiences',
            to: wp('/audiences'),
            icon: Users,
            active: isUnder('/audiences'),
          },
        ]
      : []),
    // Activations sit directly after Audiences as a PEER, never nested under it: an activation is
    // workspace-wide and one audience has many, so nesting would assert a scope that isn't real.
    // They ride the audiences flag — an activation is how an audience leaves the building, so a
    // workspace that cannot build one has nothing to activate. That is the same gate the
    // activations.* procedures are on, so the entry can never point at an API answering NOT_FOUND.
    ...(isAudiences
      ? [
          {
            label: 'Activations',
            to: wp('/activations'),
            icon: Send,
            active: isUnder('/activations'),
          },
        ]
      : []),
    {
      label: 'Context',
      to: wp('/context'),
      icon: BookOpen,
      active: isUnder('/context'),
    },
  ];

  return (
    <Sidebar variant="sidebar" collapsible="icon">
      {/* ── Header: brand + workspace + new chat ── */}
      <SidebarHeader className={cn(isCollapsed ? 'items-center gap-4 p-4' : 'gap-4 p-4')}>
        <div className="relative hidden md:block">
          {/* Collapsed: the logo doubles as the expand control, swapping to a panel icon on hover */}
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Expand sidebar"
            title="Expand sidebar"
            aria-hidden={!isCollapsed}
            tabIndex={isCollapsed ? 0 : -1}
            className={cn(
              'group/logo flex size-7 items-center justify-center rounded-md transition-[opacity,background-color] duration-200 hover:bg-grey-75',
              isCollapsed ? 'opacity-100' : 'pointer-events-none absolute inset-0 opacity-0',
            )}
          >
            <RudderstackBrandLogo
              variant="compact"
              className="size-4 min-w-0 transition-opacity duration-200 group-hover/logo:opacity-0"
            />
            <PanelLeftOpen className="absolute inset-0 m-auto size-4 text-foreground opacity-0 transition-opacity duration-200 group-hover/logo:opacity-100" />
          </button>

          {/* Expanded: full logo + collapse toggle */}
          <div
            aria-hidden={isCollapsed}
            className={cn(
              'flex h-10 items-center gap-1 transition-opacity duration-200',
              isCollapsed ? 'pointer-events-none absolute inset-0 opacity-0' : 'opacity-100',
            )}
          >
            <Link
              to={wp()}
              tabIndex={isCollapsed ? -1 : 0}
              className="flex h-8 min-w-0 flex-1 items-center rounded-md px-2"
              aria-label="Go to chat"
            >
              <RudderstackBrandLogo variant="full" className="h-4 min-w-0" />
            </Link>
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
              tabIndex={isCollapsed ? -1 : 0}
              className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-grey-75 hover:text-foreground"
            >
              <PanelLeftClose className="size-4" />
            </button>
          </div>
        </div>

        {/* Workspace switcher */}
        {isCollapsed ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={switcherLabel}
                className="flex size-7 items-center justify-center rounded-md border border-grey-100 bg-grey-25 text-muted-foreground transition-colors hover:bg-grey-0 hover:text-foreground"
              >
                <ChevronsUpDown className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <WorkspaceDropdownContent
              workspaces={workspaces}
              currentId={workspace.id}
              onSwitch={handleWorkspaceSwitch}
              onNew={() => void navigate('/new-workspace')}
              {...inviteProps}
            />
          </DropdownMenu>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={switcherLabel}
                className="flex w-full items-center gap-2 rounded-lg border border-grey-100 bg-card px-2.5 py-2 text-left text-sidebar-foreground transition-colors hover:bg-grey-50"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[0.625rem] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                    Workspace
                  </span>
                  <span className="block truncate text-[0.8125rem] font-semibold text-foreground">
                    {workspace.name}
                  </span>
                </span>
                {invites.length > 0 && (
                  <Badge variant="info" className="shrink-0" aria-hidden>
                    {invites.length}
                  </Badge>
                )}
                <ChevronsUpDown className="size-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <WorkspaceDropdownContent
              workspaces={workspaces}
              currentId={workspace.id}
              onSwitch={handleWorkspaceSwitch}
              onNew={() => void navigate('/new-workspace')}
              {...inviteProps}
            />
          </DropdownMenu>
        )}
      </SidebarHeader>

      {/* ── Content: flat nav + starred + recent chats ── */}
      <SidebarContent className={cn(isCollapsed ? 'items-center gap-5 px-4 pt-1.5' : 'gap-5 p-4')}>
        <SidebarGroup className="p-0">
          <SidebarMenu>
            {navItems.map((item) => (
              <SidebarMenuItem key={item.label}>
                <SidebarMenuButton asChild isActive={item.active} tooltip={item.label}>
                  <Link to={item.to} aria-current={item.active ? 'page' : undefined}>
                    <item.icon className="size-4" />
                    <span>{item.label}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>

        {/* Starred dashboards — hidden when collapsed */}
        {!isCollapsed && starredDashboards.length > 0 && (
          <SidebarSection
            label="Starred"
            persistKey="starred"
            activeRow={activeStarredDashboard && renderStarredRow(activeStarredDashboard)}
          >
            <SidebarMenu>{starredDashboards.map(renderStarredRow)}</SidebarMenu>
          </SidebarSection>
        )}

        {/* Recent chats — hidden when collapsed */}
        {!isCollapsed && (
          <SidebarSection
            label="Recent chats"
            persistKey="recent-chats"
            activeRow={activeRecentChat && renderRecentChatRow(activeRecentChat)}
            action={
              recentChats.length === 0 ? undefined : (
                <Link
                  to={wp('/chats')}
                  className="flex items-center gap-0.5 font-normal text-grey-300 transition-colors hover:text-foreground"
                >
                  View all
                  <ChevronRight className="size-3.5" />
                </Link>
              )
            }
          >
            {sessionsLoading ? (
              <div role="status">
                <span className="sr-only">Loading recent chats</span>
                <SidebarMenu aria-hidden>
                  {Array.from({ length: RECENT_CHATS_LIMIT }, (_, i) => (
                    <SidebarMenuItem key={i}>
                      <SidebarMenuSkeleton showIcon />
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </div>
            ) : recentChats.length > 0 ? (
              <SidebarMenu>{recentChats.map(renderRecentChatRow)}</SidebarMenu>
            ) : sessionsError ? (
              <Callout variant="error" className="py-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="flex-1">Couldn&apos;t load recent chats</span>
                  <Button
                    type="button"
                    variant="tertiary-destructive"
                    size="sm"
                    iconOnly
                    aria-label="Retry loading recent chats"
                    title="Retry"
                    Icon={<RefreshCw />}
                    loading={sessionsFetching}
                    onClick={() => void refetchSessions()}
                  />
                </div>
              </Callout>
            ) : (
              <Empty className="border p-4 md:p-4">
                <EmptyDescription className="text-xs">No chats yet</EmptyDescription>
              </Empty>
            )}
          </SidebarSection>
        )}
      </SidebarContent>

      {/* ── Footer: settings nav item + user row ── */}
      <SidebarFooter className={cn(isCollapsed ? 'items-center gap-1 px-4 pb-4' : 'gap-1 p-4')}>
        <SidebarMenu>
          {showSetupEntry && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isSetupActive}
                tooltip={`Setup, ${setup.doneCount} of ${SETUP_STEP_TOTAL}`}
              >
                <Link
                  to={wp()}
                  aria-label={`Setup, ${setup.doneCount} of ${SETUP_STEP_TOTAL} done`}
                  aria-current={isSetupActive ? 'page' : undefined}
                  onClick={() => setup.prefs.setHidden(false)}
                >
                  <ListChecks className="size-4" />
                  <span className="flex-1">Setup</span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {setup.doneCount} of {SETUP_STEP_TOTAL}
                  </span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isSettingsActive} tooltip="Settings">
              <Link to={wp('/settings')} aria-current={isSettingsActive ? 'page' : undefined}>
                <Settings className="size-4" />
                <span>Settings</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <FeatureFlagsTrigger />
        </SidebarMenu>

        {user && (
          <>
            <SidebarSeparator className="m-0" />
            {(() => {
              const displayLabel = user.displayName ?? user.githubLogin ?? 'user';
              const avatarSrc = user.avatarUrl ?? user.githubAvatarUrl;
              const firstChar = displayLabel[0]?.toUpperCase() ?? '?';
              const avatar = (
                <Avatar className="size-5 border border-border">
                  <AvatarImage src={avatarSrc ?? undefined} alt={displayLabel} />
                  <AvatarFallback className="text-[0.625rem]">{firstChar}</AvatarFallback>
                </Avatar>
              );
              return (
                <Popover>
                  <PopoverTrigger asChild>
                    {isCollapsed ? (
                      <button
                        type="button"
                        aria-label={`${displayLabel} account`}
                        className="flex size-7 items-center justify-center rounded-md transition-colors hover:bg-grey-75"
                      >
                        {avatar}
                      </button>
                    ) : (
                      <button
                        type="button"
                        aria-label={`${displayLabel} account`}
                        className="flex h-8 w-full items-center gap-2 rounded-md px-2 text-left transition-colors hover:bg-grey-75"
                      >
                        {avatar}
                        <span className="min-w-0 flex-1 truncate text-sm text-sidebar-foreground">{displayLabel}</span>
                        <span className="text-[0.625rem] text-muted-foreground/60 tabular-nums">
                          v{__APP_VERSION__}
                        </span>
                        <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground/70" />
                      </button>
                    )}
                  </PopoverTrigger>
                  {/* A Popover (not a DropdownMenu) so the real segmented ThemeToggle keeps its own
                      keyboard nav — a role="menu" would trap focus to menu items and skip it. */}
                  <PopoverContent side="top" align="start" className="min-w-50 rounded-lg p-1.5">
                    <div className="flex items-center justify-between gap-2 px-1 py-1">
                      <p className="px-0.5 text-xs font-medium text-muted-foreground">Theme</p>
                      <ThemeToggle />
                    </div>
                    <Separator className="my-1" />
                    <Button
                      variant="tertiary"
                      size="lg"
                      onClick={() => void signOutAndRedirect()}
                      Icon={<LogOut />}
                      label="Sign out"
                      className="w-full justify-start"
                    />
                  </PopoverContent>
                </Popover>
              );
            })()}
          </>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}

/** A collapsible sidebar group, open by default and remembered per browser. When closed, it still shows the current page's row. */
function SidebarSection({
  label,
  persistKey,
  action,
  activeRow,
  children,
}: {
  label: string;
  persistKey: string;
  action?: React.ReactNode;
  activeRow?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  const [open, setOpen] = useLocalStorage(`sidebar-section:${persistKey}`, true);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/section">
      <SidebarGroup className="p-0">
        <SidebarGroupLabel className="gap-2 text-grey-600">
          <CollapsibleTrigger className="flex flex-1 items-center gap-1 self-stretch rounded-md text-left ring-sidebar-ring outline-hidden transition-colors hover:text-foreground focus-visible:ring-2">
            {label}
            <ChevronDown className="size-3.5 transition-transform group-data-[state=closed]/section:-rotate-90" />
          </CollapsibleTrigger>
          {action}
        </SidebarGroupLabel>
        <CollapsibleContent>
          <SidebarGroupContent>{children}</SidebarGroupContent>
        </CollapsibleContent>
        {!open && activeRow && (
          <SidebarGroupContent>
            <SidebarMenu>{activeRow}</SidebarMenu>
          </SidebarGroupContent>
        )}
      </SidebarGroup>
    </Collapsible>
  );
}

/**
 * Shared workspace dropdown content used in both expanded and collapsed modes. A workspace that the RudderStack
 * membership check denies (ACT2-1124 §5.7) shows its badge and cannot be picked; its tooltip gives the reason.
 */
function WorkspaceDropdownContent({
  workspaces,
  currentId,
  onSwitch,
  onNew,
  invites,
  onAcceptInvite,
  acceptPending,
}: {
  workspaces: { id: string; name: string; rudderstackAccessDenied?: RudderstackAccessDenial | null }[];
  currentId?: string;
  onSwitch: (id: string) => void;
  onNew: () => void;
  /** The person's live invites (ACT2-1124): a person who stays in their workspace still sees them here. */
  invites: PendingInvite[];
  onAcceptInvite: (inviteId: string) => void;
  acceptPending: boolean;
}): React.ReactElement {
  return (
    <DropdownMenuContent className="min-w-56 rounded-lg p-1.5" align="start">
      {workspaces.map((ws) => {
        const denial = ws.rudderstackAccessDenied ?? null;
        if (denial) {
          // A disabled item gets no pointer events, so a wrapper holds the tooltip.
          return (
            <Tooltip key={ws.id}>
              <TooltipTrigger asChild>
                <div>
                  <DropdownMenuItem disabled className="rounded-md px-2.5 py-1.5">
                    <span className="truncate">{ws.name}</span>
                    <Badge variant="warning" className="ml-auto">
                      {accessDenialBadge(denial)}
                    </Badge>
                  </DropdownMenuItem>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                {denial.message}
              </TooltipContent>
            </Tooltip>
          );
        }
        return (
          <DropdownMenuItem key={ws.id} className="rounded-md px-2.5 py-1.5" onClick={() => onSwitch(ws.id)}>
            <span className="truncate">{ws.name}</span>
            {ws.id === currentId && <Check className="ml-auto size-4" />}
          </DropdownMenuItem>
        );
      })}
      {invites.length > 0 && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="px-2.5 py-1 text-xs font-medium text-muted-foreground">
            Invites
          </DropdownMenuLabel>
          {invites.map((invite) => (
            <DropdownMenuItem
              key={invite.id}
              className="rounded-md px-2.5 py-1.5"
              disabled={acceptPending}
              onClick={() => onAcceptInvite(invite.id)}
            >
              <span className="truncate">{invite.workspaceName}</span>
              <Badge variant="info" className="ml-auto shrink-0">
                {`Accept as ${inviteRoleLabel(invite.role)}`}
              </Badge>
            </DropdownMenuItem>
          ))}
        </>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem className="rounded-md px-2.5 py-1.5 text-muted-foreground" onClick={onNew}>
        <Plus className="mr-2 size-4" />
        Create workspace
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}
